import { isValidElement, memo, useCallback, useMemo, useState } from "react";
import type { ReactNode } from "react";
import { Link } from "react-router-dom";
import ReactMarkdown from "react-markdown";
import type { Components } from "react-markdown";
import remarkGfm from "remark-gfm";
import rehypeSanitize from "rehype-sanitize";
import rehypeSlug from "rehype-slug";
import { resolveImageSrc, sanitizeSchema } from "../../lib/markdown";
import { ImageZoomOverlay } from "./ImageZoomOverlay";

interface Props {
  readonly markdown: string;
  /** Optional resolver mapping relative `.md` paths to in-app document ids. */
  readonly resolveDocLink?: (href: string) => string | undefined;
  /**
   * Maps a resolved in-app document id to a router path. Defaults to the
   * preview route (`/doc/:id`); the console passes its run-scoped report route
   * so the same shared renderer powers both apps without route coupling.
   */
  readonly docHref?: (id: string) => string;
  /**
   * Base URL for resolving **relative** image sources (e.g. the embedded
   * diagram PNGs written as `./diagrams/<name>.png`). A Knowledge View document
   * is fetched from `…/views/<view>/README.md`, but the SPA route is
   * `/views/<view>`, so a browser would resolve `./diagrams/…` against the
   * route, not the file. Passing the document's own directory here rewrites
   * relative image URLs to absolute content URLs. Absolute/data URLs are left
   * untouched.
   */
  readonly imageBase?: string;
  /**
   * Enable click-to-zoom on embedded images (diagrams). Clicking an image opens
   * a full-screen lightbox with a dimmed backdrop; Escape or a click closes it.
   * Defaults to `true`.
   */
  readonly enableImageZoom?: boolean;
  /**
   * What to do with an `![...](...)` embed.
   *
   * `"render"` (default) is what a report needs: its diagrams are images, and
   * they are written by CloudoX into the same content tree the document came
   * from.
   *
   * `"omit"` is for Markdown whose image list is not CloudoX's. An Agent answer
   * has no diagrams, so every image in one arrived from prose — and an image is
   * the one Markdown construct a browser fetches without being asked. A single
   * `![](https://elsewhere/p.png?q=…)` is a read receipt, an IP address, and a
   * channel out, all before the reader has decided to trust the answer. The alt
   * text is kept so the omission is visible rather than silent.
   */
  readonly imagePolicy?: "render" | "omit";
  /**
   * What to do with a link to somewhere outside the app.
   *
   * `"open"` (default) makes it a real link in a new tab, which is right for
   * report prose: its links are written by CloudoX from the knowledge graph.
   *
   * `"reveal"` renders the destination as text instead. It costs a reader one
   * copy-paste and takes away the one-click phishing target that a link whose
   * label and destination were both chosen by a model otherwise is.
   */
  readonly externalLinkPolicy?: "open" | "reveal";
}

const defaultDocHref = (id: string): string => `/doc/${encodeURIComponent(id)}`;

/**
 * Renders Markdown safely.
 *
 * Pipeline order matters:
 *   remark-gfm   — adds tables, task lists, strikethrough, autolinks.
 *   rehype-slug  — assigns deterministic ids to headings.
 *   rehype-sanitize — final defence: strips everything not on the schema.
 *
 * `react-markdown` already escapes raw HTML; sanitization is belt-and-braces
 * to keep the boundary explicit and future-proof if anyone ever turns on
 * `skipHtml: false` or pipes in HTML from another source.
 *
 * Section permalinks are rendered here as React components rather than by
 * `rehype-autolink-headings`. The plugin injected the anchor *before*
 * `rehype-sanitize`, which dropped its `class`, `aria-hidden`, and `tabindex` —
 * so every section heading announced as "<title> #" and the glyph never picked
 * up its hover styling. React output is not sanitized, so building the anchor in
 * {@link Permalink} keeps the ARIA wiring intact by construction.
 */
function MarkdownContentImpl({
  markdown,
  resolveDocLink,
  docHref = defaultDocHref,
  imageBase,
  enableImageZoom = true,
  imagePolicy = "render",
  externalLinkPolicy = "open",
}: Props) {
  const [zoomed, setZoomed] = useState<{
    src: string;
    alt: string;
    caption: string;
  } | null>(null);
  const openZoom = useCallback(
    (src: string | undefined, alt: string, from: HTMLElement) => {
      if (src) setZoomed({ src, alt, caption: captionFor(from) });
    },
    [],
  );
  const closeZoom = useCallback(() => setZoomed(null), []);

  const plugins = useMemo(
    () => ({
      remarkPlugins: [remarkGfm],
      rehypePlugins: [rehypeSlug, [rehypeSanitize, sanitizeSchema]],
    }),
    [],
  );

  // Memoized because `react-markdown` treats each entry as a component *type*.
  // Rebuilding the map every render gives every element a new type, so React
  // unmounts and re-creates the whole document on any state change here —
  // including opening the lightbox, which destroyed the very image the overlay
  // has to return focus to when it closes.
  const components = useMemo<Components>(
    () => ({
      // Section headings carry a deep-link permalink. The h1 is the document
      // title — unique per page and already the target of the page URL — so
      // it gets none.
      h2: ({ children, id }) => (
        <SectionHeading as="h2" id={id}>
          {children}
        </SectionHeading>
      ),
      h3: ({ children, id }) => (
        <SectionHeading as="h3" id={id}>
          {children}
        </SectionHeading>
      ),
      h4: ({ children, id }) => (
        <SectionHeading as="h4" id={id}>
          {children}
        </SectionHeading>
      ),
      // Wide tables (e.g. resource inventories full of ARNs) scroll
      // horizontally within the content column instead of bleeding past it.
      //
      // The scroll container is a focus stop. A pointer can drag it; without a
      // tab stop a keyboard cannot reach the columns that overflow, so the
      // Reference tables' right-hand columns were unreachable without a mouse.
      // `group` rather than `region` because every table would otherwise add a
      // landmark, burying the page's real ones.
      table: ({ children, ...rest }) => (
        <div
          className="doc-table-scroll"
          tabIndex={0}
          role="group"
          aria-label="Table (scrolls horizontally)"
        >
          <table {...rest}>{children}</table>
        </div>
      ),
      img: ({ src, alt, ...rest }) => {
        if (imagePolicy === "omit") {
          // Named, not hidden: a reader who is shown "[image omitted]"
          // knows the answer contained one, which is also the signal worth
          // reporting if it ever happens.
          //
          // Returned before the source is resolved, so an omitted image never
          // becomes a URL this component has computed and could later hand to
          // something that fetches it.
          return (
            <span className="doc-image-omitted">
              {alt ? `[image omitted: ${alt}]` : "[image omitted]"}
            </span>
          );
        }
        const resolved = resolveImageSrc(
          typeof src === "string" ? src : undefined,
          imageBase,
        );
        if (!enableImageZoom) {
          return (
            <img src={resolved} alt={alt ?? ""} loading="lazy" {...rest} />
          );
        }
        return (
          <img
            src={resolved}
            alt={alt ?? ""}
            loading="lazy"
            role="button"
            tabIndex={0}
            aria-label={alt ? `Enlarge image: ${alt}` : "Enlarge image"}
            style={{ cursor: "zoom-in" }}
            onClick={(e) => openZoom(resolved, alt ?? "", e.currentTarget)}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                openZoom(resolved, alt ?? "", e.currentTarget);
              }
            }}
            {...rest}
          />
        );
      },
      a: ({ href, children, ...rest }) => {
        if (!href) return <a {...rest}>{children}</a>;
        if (href.startsWith("#")) return <a href={href}>{children}</a>;

        const isExternal = /^https?:\/\//i.test(href);
        if (!isExternal) {
          const docId = resolveDocLink?.(href);
          if (docId) {
            return <Link to={docHref(docId)}>{children}</Link>;
          }
        }
        if (isExternal && externalLinkPolicy === "reveal") {
          return (
            <span className="doc-link-revealed">
              {children} ({href})
            </span>
          );
        }
        return (
          <a
            href={href}
            {...(isExternal
              ? { target: "_blank", rel: "noopener noreferrer nofollow" }
              : {})}
          >
            {children}
          </a>
        );
      },
    }),
    // Both policies belong here for the same reason the callbacks do: this map
    // is memoized, so a policy left out of the list would keep rendering
    // through the component types captured under the previous one. A surface
    // that switched to the restrictive policy would go on rendering images.
    [
      resolveDocLink,
      docHref,
      imageBase,
      enableImageZoom,
      openZoom,
      imagePolicy,
      externalLinkPolicy,
    ],
  );

  return (
    <article className="doc-prose">
      <ReactMarkdown
        skipHtml
        remarkPlugins={plugins.remarkPlugins}
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        rehypePlugins={plugins.rehypePlugins as any}
        components={components}
      >
        {markdown}
      </ReactMarkdown>
      {zoomed ? (
        <ImageZoomOverlay
          src={zoomed.src}
          alt={zoomed.alt}
          caption={zoomed.caption}
          onClose={closeZoom}
        />
      ) : null}
    </article>
  );
}

export const MarkdownContent = memo(MarkdownContentImpl);

/**
 * The caption belonging to a figure, read from the rendered document.
 *
 * Both diagram paths emit the caption as a blockquote immediately beneath the
 * image, so it arrives as a *sibling* of the image's paragraph rather than as
 * anything the `img` renderer can see. Reading it from the DOM at the moment of
 * the click keeps the enlarged view showing exactly the caption the reader can
 * see under the figure — it cannot drift from it, because it is it.
 *
 * Returns "" when the figure has no caption, which is a legitimate case: not
 * every embedded image is a CloudoX diagram.
 */
function captionFor(image: HTMLElement): string {
  const block = image.closest("p") ?? image;
  const next = block.nextElementSibling;
  if (!next || next.tagName !== "BLOCKQUOTE") return "";
  return (next.textContent ?? "").trim();
}

/**
 * A section heading and its deep-link permalink.
 *
 * The permalink is a real, keyboard-reachable link with its own name
 * ("Permalink to <section>") — hiding it from assistive tech, as the previous
 * implementation intended, would have made the deep link mouse-only. But a named
 * link *inside* a heading becomes part of the heading's own accessible name, so
 * the heading is named explicitly from the span holding just its title. That is
 * what keeps the heading announcing as "Top Findings & Recommendations" rather
 * than "Top Findings & Recommendations, permalink to Top Findings…".
 */
function SectionHeading({
  as: Tag,
  id,
  children,
}: {
  readonly as: "h2" | "h3" | "h4";
  readonly id: string | undefined;
  readonly children: ReactNode;
}) {
  if (!id) return <Tag>{children}</Tag>;
  const textId = `${id}-text`;
  const title = headingText(children);
  return (
    <Tag id={id} aria-labelledby={textId}>
      <span id={textId}>{children}</span>
      <a
        href={`#${id}`}
        className="anchor-link"
        aria-label={
          title ? `Permalink to ${title}` : "Permalink to this section"
        }
      >
        <span aria-hidden="true">#</span>
      </a>
    </Tag>
  );
}

/** The plain text of a heading, for the permalink's accessible name. */
function headingText(node: ReactNode): string {
  if (node === null || node === undefined || typeof node === "boolean")
    return "";
  if (typeof node === "string" || typeof node === "number") return String(node);
  if (Array.isArray(node)) return node.map(headingText).join("");
  if (isValidElement<{ children?: ReactNode }>(node)) {
    return headingText(node.props.children);
  }
  return "";
}
